import { useOverlay } from '@abyss/web/hooks/useOverlay';
import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { CompareProvider } from '#/models/Provider';
import find from 'lodash/find';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../../../../common/ConstantsLagoon';
import { useLagoon } from '../../../../../../../hooks/useLagoon';
import { shouldUseGehaV2Styling } from '../../../../../../../utils/gehaThemeUtils';
import { DrawerMenuButton } from '../../../DrawerMenuButton';
import { PrintButton } from '../../../PrintButton';
import { ShareButton } from '../../../ShareButton';
import { adobeCompareDrawerLinkLocation } from '../../utility/compareDrawerConstants';
import { ConfirmationModal } from '../ConfirmationModal';
import { EditCompareButton } from '../EditCompare/EditCompareButton';

export const CompareDrawerHeaderStyle = styled('div', {
  variants: {
    view: {
      mobile: {
        paddingLeft: '10px',
        paddingRight: '10px',
      },
      desktop: {
        paddingLeft: '30px',
        paddingRight: '30px',
      },
    },
  },
});
type Props = {
  handleOnClickCarret: any;
  handleFocusIn: any;
  handleTabOutOfSelection: any;
  isExpandedCardOpen: boolean;
  caretRef: React.RefObject<HTMLButtonElement>;
  selectedCount: number;
  total: number;
  handleClickShare: any;
  isEditCompareOpen: boolean;
  setIsEditCompareOpen: any;
  setNameOfButtonClickedLast: any;
  showMergedCompareAndShare?: boolean;
  deselectAllButtonRef: React.RefObject<HTMLButtonElement>;
  setCompareShareToggle?: (a: boolean) => void;
  setOpenCompare?: (a: boolean) => void;
  setSelectedCheckbox?: (a: { checked: {} }) => void;
  setSelectedItems?: (a: CompareProvider[]) => void;
};

export const CompareDrawerHeader = ({
  handleOnClickCarret,
  handleFocusIn,
  handleTabOutOfSelection,
  isExpandedCardOpen,
  caretRef,
  selectedCount,
  total,
  handleClickShare,
  isEditCompareOpen,
  setIsEditCompareOpen,
  setNameOfButtonClickedLast,
  deselectAllButtonRef,
  showMergedCompareAndShare,
  setCompareShareToggle,
  setOpenCompare,
  setSelectedCheckbox,
  setSelectedItems,
}: Props) => {
  const { t } = useTranslation();
  const modalSCCompare = useOverlay('compare-drawer-header-confirmation-modal');
  const lagoonData = useLagoon('ui-messaging')();
  const compare_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE_PROVIDERS,
  })?.message;

  useEffect(() => {
    if (isEditCompareOpen) {
      deselectAllButtonRef?.current?.focus();
    } else {
      caretRef?.current?.focus();
    }
  }, [isEditCompareOpen]);

  useEffect(() => {
    if (isExpandedCardOpen) {
      caretRef?.current?.focus();
    }
  }, [isExpandedCardOpen]);

  const isGEHAV2 = shouldUseGehaV2Styling();
  const additionalIconCss = isGEHAV2 ? '$primary1' : '';

  return (
    <CompareDrawerHeaderStyle
      css={{
        paddingLeft: showMergedCompareAndShare ? '0px' : undefined,
        paddingRight: showMergedCompareAndShare ? '0px' : undefined,
      }}
      view="desktop"
    >
      <Box
        css={{
          'abyss-box-root': {
            backgroundColor: 'transparent',
            padding: '0px',
          },
        }}
      >
        <Flex
          alignItems="flex-start"
          css={{
            'abyss-flex-root': {
              marginTop: '26px',
              marginLeft: showMergedCompareAndShare ? '96px' : '0px',
              marginRight: showMergedCompareAndShare ? '96px' : '0px',
              '@media (min-width: 744px) and (max-width: 1247px)': {
                marginLeft: '10px',
                marginRight: '10px',
              },
              '@media (min-width: 360px) and (max-width: 740px)': {
                marginLeft: '10px',
                marginRight: '10px',
              },
            },
          }}
          justify="space-between"
        >
          {/* <Layout.Stack css={{ paddingTop: '4px' }}> */}
          <Layout.Group
            data-auto-testid="selection-show-mini-cards"
            data-testid="selection-show-mini-cards"
            onFocus={() => handleFocusIn()}
            onKeyDown={(e) => handleTabOutOfSelection(e)}
          >
            <Heading
              color={additionalIconCss ?? '$gray8'}
              display="h6"
              offset={1}
            >
              {compare_provider}
            </Heading>
            <Text color={additionalIconCss ?? '$gray7'}>
              ({selectedCount} {t('of')} {total} {t('SELECTED')})
            </Text>
            {!showMergedCompareAndShare && (
              <Button
                after={
                  <IconMaterial
                    data-auto-testid="compare-providers-icon-button-to-expand-and-close-compare-drawer"
                    data-testid="compare-providers-icon-button-to-expand-and-close-compare-drawer"
                    icon={
                      isExpandedCardOpen
                        ? 'keyboard_arrow_up'
                        : 'keyboard_arrow_down'
                    }
                    size="$md"
                  />
                }
                aria-expanded={Boolean(isExpandedCardOpen)}
                css={{ fontSize: 14, fontWeight: 600 }}
                data-auto-testid="compare-providers-button-to-expand-and-close-compare-drawer"
                data-testid="compare-providers-button-to-expand-and-close-compare-drawer"
                onClick={handleOnClickCarret}
                ref={caretRef}
                size="$sm"
                tabIndex={0}
                variant="tertiary"
              >
                {isExpandedCardOpen
                  ? t('COMPARE.HIDE_DETAILS')
                  : t('COMPARE.SHOW_DETAILS')}
              </Button>
            )}
          </Layout.Group>
          {/* </Layout.Stack> */}
          <Layout.Group alignItems="top">
            {isExpandedCardOpen && (
              <div>
                <PrintButton
                  locationForAnalytics={`modal:${adobeCompareDrawerLinkLocation}`}
                  selectedCount={selectedCount}
                  view="desktop"
                />
              </div>
            )}

            {isExpandedCardOpen && (
              <div>
                <ShareButton
                  data-auto-testid="compare-drawer-share-button"
                  data-testid="compare-drawer-share-button"
                  onClick={() => handleClickShare()}
                  view="none"
                />
              </div>
            )}
            <div style={{ margin: '0px 4px 4px 4px' }}>
              <DrawerMenuButton
                ariaLabel="Close"
                data-auto-testid="mapview-compare-drawer-menu"
                data-testid="mapview-compare-drawer-menu"
                icon="close"
                locationForAnalytics="modal:compare drawer"
                onClick={() => {
                  modalSCCompare?.open();
                }}
                showMergedCompareAndShare={showMergedCompareAndShare}
                view="desktop"
              />
            </div>
          </Layout.Group>
        </Flex>
        {isExpandedCardOpen && (
          <EditCompareButton
            caretRef={caretRef}
            isEditCompareOpen={isEditCompareOpen}
            setIsEditCompareOpen={setIsEditCompareOpen}
            setNameOfButtonClickedLast={setNameOfButtonClickedLast}
          />
        )}
      </Box>
      <ConfirmationModal
        handleClickCb={() => {
          setCompareShareToggle?.(false);
          setOpenCompare?.(false);
        }}
        modal={modalSCCompare}
        modalName={'compare-drawer-header-confirmation-modal'}
        setSelectedCheckbox={setSelectedCheckbox}
        setSelectedItems={setSelectedItems}
      />
    </CompareDrawerHeaderStyle>
  );
};
